import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, Table, TableHead,
  TableRow, TableCell, TableBody, TableFooter, Tabs, Tab, MenuItem,
  TextField, CircularProgress, Chip, Tooltip, IconButton, Paper
} from '@mui/material';
import {
  Assessment, Home, Download, Print, Search, FilterList
} from '@mui/icons-material';
import axios from 'axios';
import { fmt as fmtBase, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt  = fmtBase;
const fmtN = (n) => (n === null || n === undefined) ? '—' : fmt(n);

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const TabPanel = ({ children, value, index }) =>
  value === index ? <Box pt={2}>{children}</Box> : null;

const TotalsRow = ({ cells, label = 'TOTAL' }) => (
  <TableRow sx={{ bgcolor: '#263238', '& td': { color: '#fff', fontWeight: 700, fontSize: '0.8rem' } }}>
    <TableCell>{label}</TableCell>
    {cells.map((c, i) => <TableCell key={i} align="right">{c}</TableCell>)}
  </TableRow>
);

// ─── Register Tab ──────────────────────────────────────────────────────────────
const RegisterTab = ({ runNo }) => {
  const navigate   = useNavigate();
  const [data,    setData]    = useState([]);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!runNo) return;
    setLoading(true); setError('');
    axios.get(`${API}/api/payroll/runs/${runNo}/register`, { headers: hdrs() })
      .then(r => setData(r.data || []))
      .catch(() => setError('Failed to load register'))
      .finally(() => setLoading(false));
  }, [runNo]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return data.filter(e => !q ||
      e.employeeName?.toLowerCase().includes(q) ||
      e.employeeNo?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q));
  }, [data, search]);

  const totals = useMemo(() => ({
    gross:   filtered.reduce((s, e) => s + (e.grossPay     || 0), 0),
    paye:    filtered.reduce((s, e) => s + (e.paye         || 0), 0),
    shif:    filtered.reduce((s, e) => s + (e.shif         || 0), 0),
    hl:      filtered.reduce((s, e) => s + (e.housingLevyEE|| 0), 0),
    nssf:    filtered.reduce((s, e) => s + (e.nssf         || 0), 0),
    other:   filtered.reduce((s, e) => s + (e.totalDeductions - (e.paye||0) - (e.shif||0) - (e.housingLevyEE||0) - (e.nssf||0) || 0), 0),
    net:     filtered.reduce((s, e) => s + (e.netPay       || 0), 0),
  }), [filtered]);

  if (!runNo) return <Alert severity="info">Select a run above.</Alert>;
  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
        <TextField placeholder="Search name, no, dept…" size="small" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 240 }}
          InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 0.5 }} /> }} />
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<Print />} onClick={() => window.print()} className="no-print">
            Print
          </Button>
        </Box>
      </Box>
      <CompanyHeader title="Payroll Report" />
      <Paper elevation={1} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Emp No</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Dept</strong></TableCell>
              <TableCell align="right"><strong>Gross Pay</strong></TableCell>
              <TableCell align="right"><strong>PAYE</strong></TableCell>
              <TableCell align="right"><strong>SHIF</strong></TableCell>
              <TableCell align="right"><strong>H. Levy</strong></TableCell>
              <TableCell align="right"><strong>NSSF</strong></TableCell>
              <TableCell align="right"><strong>Other Ded.</strong></TableCell>
              <TableCell align="right"><strong>Net Pay</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center">
                <Typography variant="body2" color="text.secondary" py={2}>No entries</Typography>
              </TableCell></TableRow>
            ) : filtered.map(e => {
              const otherDed = (e.totalDeductions||0) - (e.paye||0) - (e.shif||0) - (e.housingLevyEE||0) - (e.nssf||0);
              return (
                <TableRow key={e.employeeNo} hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/payroll/runs/${runNo}/payslip/${e.employeeNo}`)}>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{e.employeeNo}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{e.employeeName}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{e.department || '—'}</Typography></TableCell>
                  <TableCell align="right">{fmt(e.grossPay)}</TableCell>
                  <TableCell align="right">{fmt(e.paye)}</TableCell>
                  <TableCell align="right">{fmt(e.shif)}</TableCell>
                  <TableCell align="right">{fmt(e.housingLevyEE)}</TableCell>
                  <TableCell align="right">{fmt(e.nssf)}</TableCell>
                  <TableCell align="right">{fmt(otherDed)}</TableCell>
                  <TableCell align="right"><strong>{fmt(e.netPay)}</strong></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TotalsRow cells={['', '', fmt(totals.gross), fmt(totals.paye), fmt(totals.shif), fmt(totals.hl), fmt(totals.nssf), fmt(totals.other), fmt(totals.net)]} />
          </TableFooter>
        </Table>
      </Paper>
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        {filtered.length} employee{filtered.length !== 1 ? 's' : ''} · Click a row to view payslip
      </Typography>
    </>
  );
};

// ─── Statutory Tab ─────────────────────────────────────────────────────────────
const StatutoryTab = ({ runNo }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!runNo) return;
    setLoading(true); setError('');
    axios.get(`${API}/api/payroll/runs/${runNo}/statutory`, { headers: hdrs() })
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load statutory summary'))
      .finally(() => setLoading(false));
  }, [runNo]);

  if (!runNo) return <Alert severity="info">Select a run above.</Alert>;
  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error">{error}</Alert>;
  if (!data)   return null;

  const rows = [
    { label: 'PAYE',               note: 'Employee',        value: data.paye },
    { label: 'SHIF',               note: 'Employee 2.75%',  value: data.shif },
    { label: 'Housing Levy (EE)',   note: 'Employee 1.5%',   value: data.housingLevyEE },
    { label: 'Housing Levy (ER)',   note: 'Employer 1.5%',   value: data.housingLevyER },
    { label: 'NSSF Employee',       note: 'Tier I + II',     value: data.nssf },
    { label: 'NSSF Employer',       note: 'Tier I + II',     value: data.nssfER },
  ];
  const grandTotal = rows.reduce((s, r) => s + (r.value || 0), 0);

  return (
    <Paper elevation={1} sx={{ maxWidth: 600 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell><strong>Statutory Type</strong></TableCell>
            <TableCell><strong>Note</strong></TableCell>
            <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} hover>
              <TableCell><Typography variant="body2" fontWeight={600}>{r.label}</Typography></TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{r.note}</Typography></TableCell>
              <TableCell align="right">{fmt(r.value)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow sx={{ bgcolor: '#263238' }}>
            <TableCell colSpan={2} sx={{ color: '#fff', fontWeight: 700 }}>TOTAL STATUTORY</TableCell>
            <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>{fmt(grandTotal)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">
          Head Count: {data.headCount} · Gross Pay: {sym()} {fmt(data.grossPay)} · Net Pay: {sym()} {fmt(data.netPay)}
        </Typography>
      </Box>
    </Paper>
  );
};

// ─── By Component Tab ──────────────────────────────────────────────────────────
const ByComponentTab = ({ runNo }) => {
  const [code,    setCode]    = useState('');
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const fetch = useCallback(() => {
    if (!runNo || !code.trim()) return;
    setLoading(true); setError('');
    axios.get(`${API}/api/payroll/runs/${runNo}/by-component`, {
      params: { code: code.trim().toUpperCase() }, headers: hdrs()
    })
      .then(r => setData(r.data || []))
      .catch(() => setError('Failed to load component data'))
      .finally(() => setLoading(false));
  }, [runNo, code]);

  if (!runNo) return <Alert severity="info">Select a run above.</Alert>;

  return (
    <>
      <Box display="flex" gap={2} alignItems="flex-start" mb={2} flexWrap="wrap">
        <TextField label="Component Code" size="small" value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. BASIC, HOUSE, PAYE, SACCO"
          InputLabelProps={{ shrink: true }}
          onKeyDown={e => e.key === 'Enter' && fetch()}
          sx={{ minWidth: 220 }} />
        <Button variant="contained" startIcon={<FilterList />} onClick={fetch}
          disabled={!code.trim() || loading}
          sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' }, mt: 0.1 }}>
          Filter
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {loading && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
      {!loading && data.length > 0 && (() => {
        // Flatten: each entry has { employeeNo, employeeName, department, lines:[...], total }
        const flat = data.flatMap(e => (e.lines || []).map(l => ({
          employeeNo:   e.employeeNo,
          employeeName: e.employeeName,
          department:   e.department,
          code:         l.code,
          description:  l.description,
          amount:       l.amount,
        })));
        const grandTotal = flat.reduce((s, r) => s + (r.amount || 0), 0);
        return (
          <Paper elevation={1} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Emp No</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Dept</strong></TableCell>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flat.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{r.employeeNo}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.employeeName}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.department || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{r.code}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.description}</Typography></TableCell>
                    <TableCell align="right"><strong>{fmt(r.amount)}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: '#263238' }}>
                  <TableCell colSpan={5} sx={{ color: '#fff', fontWeight: 700 }}>
                    TOTAL — {code} ({data.length} employee{data.length !== 1 ? 's' : ''})
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>{fmt(grandTotal)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>
        );
      })()}
      {!loading && data.length === 0 && code && (
        <Alert severity="info">No entries found for code "{code}" in this run.</Alert>
      )}
    </>
  );
};

// ─── P9A Tab ───────────────────────────────────────────────────────────────────
const P9ATab = ({ runNo }) => {
  const [employees, setEmployees] = useState([]);
  const [empNo,     setEmpNo]     = useState('');
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [empLoading, setEmpLoading] = useState(true);
  const [error,     setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/employees`, { headers: hdrs() })
      .then(r => setEmployees(r.data || []))
      .catch(() => {})
      .finally(() => setEmpLoading(false));
  }, []);

  const load = useCallback(() => {
    if (!runNo || !empNo) return;
    setLoading(true); setError(''); setData(null);
    axios.get(`${API}/api/payroll/runs/${runNo}/p9a/${empNo}`, { headers: hdrs() })
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load P9A data'))
      .finally(() => setLoading(false));
  }, [runNo, empNo]);

  useEffect(() => { if (empNo && runNo) load(); }, [empNo, runNo, load]);

  if (!runNo) return <Alert severity="info">Select a run above.</Alert>;

  return (
    <>
      <Box mb={2} maxWidth={400}>
        <TextField select label="Employee" size="small" fullWidth value={empNo}
          onChange={e => setEmpNo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
          disabled={empLoading}>
          <MenuItem value=""><em>{empLoading ? 'Loading…' : '— Select employee —'}</em></MenuItem>
          {employees.map(e => (
            <MenuItem key={e.employeeNo} value={e.employeeNo}>
              {e.fullName || `${e.firstName} ${e.lastName}`} ({e.employeeNo})
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {loading && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}

      {!loading && data && (() => {
        // Build 12-month grid: periodCode format is e.g. 'JAN-2026'
        const PCODE_MONTH = {JAN:1,FEB:2,MAR:3,APR:4,MAY:5,JUN:6,JUL:7,AUG:8,SEP:9,OCT:10,NOV:11,DEC:12};
        const monthlyMap = {};
        (data.monthly || []).forEach(m => {
          const key = m.periodCode?.split('-')[0];
          if (key && PCODE_MONTH[key]) monthlyMap[PCODE_MONTH[key]] = m;
        });
        const grid = Array.from({ length: 12 }, (_, i) => ({ monthNo: i + 1, ...monthlyMap[i + 1] }));

        return (
          <>
            <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>{data.employeeName}</Typography>
              <Typography variant="caption" color="text.secondary">
                Emp No: {data.employeeNo} · Year: {data.year}
                {data.kraPin ? ` · Revenue Authority No.: ${data.kraPin}` : ''}
                {data.nssfNo ? ` · NSSF: ${data.nssfNo}` : ''}
              </Typography>
            </Box>

            <Paper elevation={1} sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Month</strong></TableCell>
                    <TableCell align="right"><strong>Gross Pay</strong></TableCell>
                    <TableCell align="right"><strong>Taxable Pay</strong></TableCell>
                    <TableCell align="right"><strong>PAYE</strong></TableCell>
                    <TableCell align="right"><strong>SHIF</strong></TableCell>
                    <TableCell align="right"><strong>Net Pay</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grid.map((m) => {
                    const has = !!m.grossPay;
                    return (
                      <TableRow key={m.monthNo} hover sx={{ bgcolor: has ? 'inherit' : 'grey.50' }}>
                        <TableCell>
                          <Typography variant="body2" color={has ? 'text.primary' : 'text.disabled'}>
                            {MONTHS[m.monthNo - 1]} {data.year}
                          </Typography>
                        </TableCell>
                        <TableCell align="right"><Typography variant="body2" color={has ? 'inherit' : 'text.disabled'}>{has ? fmt(m.grossPay)    : '—'}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2" color={has ? 'inherit' : 'text.disabled'}>{has ? fmt(m.taxablePay)  : '—'}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2" color={has ? 'inherit' : 'text.disabled'}>{has ? fmt(m.paye)        : '—'}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2" color={has ? 'inherit' : 'text.disabled'}>{has ? fmt(m.shif)        : '—'}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2" fontWeight={has ? 700 : 400} color={has ? 'inherit' : 'text.disabled'}>{has ? fmt(m.netPay) : '—'}</Typography></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#263238' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>YTD TOTAL</TableCell>
                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>{fmt(data.ytd?.grossPay)}</TableCell>
                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>{fmt(data.ytd?.taxablePay)}</TableCell>
                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>{fmt(data.ytd?.paye)}</TableCell>
                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>{fmt(data.ytd?.shif)}</TableCell>
                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>{fmt(data.ytd?.netPay)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </Paper>

            <Box display="flex" justifyContent="flex-end" mt={1.5} gap={1} className="no-print">
              <Button size="small" startIcon={<Print />} onClick={() => window.print()}>Print P9A</Button>
            </Box>
          </>
        );
      })()}
    </>
  );
};

// ─── Bank File Tab ─────────────────────────────────────────────────────────────
const BankFileTab = ({ runNo }) => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!runNo) return;
    setLoading(true); setError('');
    axios.get(`${API}/api/payroll/runs/${runNo}/entries`, { headers: hdrs() })
      .then(r => setData(r.data || []))
      .catch(() => setError('Failed to load entries'))
      .finally(() => setLoading(false));
  }, [runNo]);

  const downloadCSV = () => {
    const header = 'Employee No,Employee Name,Bank Name,Account No,Branch,Net Pay';
    const rows = data.map(e =>
      `"${e.employeeNo}","${e.employeeName}","${e.bankName || ''}","${e.bankAccount || ''}","${e.bankBranch || ''}",${e.netPay || 0}`
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `bank-file-${runNo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!runNo) return <Alert severity="info">Select a run above.</Alert>;
  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error">{error}</Alert>;

  const missing = data.filter(e => !e.bankAccount);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="body2" fontWeight={600}>{data.length} employees</Typography>
          {missing.length > 0 && (
            <Typography variant="caption" color="error.main">
              {missing.length} employee(s) missing bank account — excluded from file
            </Typography>
          )}
        </Box>
        <Button variant="contained" startIcon={<Download />} onClick={downloadCSV}
          disabled={data.length === 0}
          sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
          Download CSV
        </Button>
      </Box>

      <Paper elevation={1} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Emp No</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Bank</strong></TableCell>
              <TableCell><strong>Account No</strong></TableCell>
              <TableCell><strong>Branch</strong></TableCell>
              <TableCell align="right"><strong>Net Pay ({sym()})</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((e, i) => (
              <TableRow key={i} hover sx={{ bgcolor: !e.bankAccount ? 'error.50' : 'inherit' }}>
                <TableCell><Typography variant="caption" fontFamily="monospace">{e.employeeNo}</Typography></TableCell>
                <TableCell><Typography variant="body2">{e.employeeName}</Typography></TableCell>
                <TableCell><Typography variant="body2">{e.bankName || <em style={{ color: '#999' }}>Missing</em>}</Typography></TableCell>
                <TableCell>
                  {e.bankAccount
                    ? <Typography variant="body2" fontFamily="monospace">{e.bankAccount}</Typography>
                    : <Chip label="Missing" size="small" color="error" variant="outlined" />}
                </TableCell>
                <TableCell><Typography variant="body2">{e.bankBranch || '—'}</Typography></TableCell>
                <TableCell align="right"><strong>{fmt(e.netPay)}</strong></TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ bgcolor: '#263238' }}>
              <TableCell colSpan={5} sx={{ color: '#fff', fontWeight: 700 }}>TOTAL NET PAY</TableCell>
              <TableCell align="right" sx={{ color: '#fff', fontWeight: 700 }}>
                {fmt(data.reduce((s, e) => s + (e.netPay || 0), 0))}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </>
  );
};

// ─── A-Third Rule Tab ──────────────────────────────────────────────────────────
const AThirdRuleTab = ({ runNo }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!runNo) return;
    setLoading(true); setError('');
    axios.get(`${API}/api/payroll/runs/${runNo}/entries`, { headers: hdrs() })
      .then(r => setEntries(r.data || []))
      .catch(() => setError('Failed to load entries'))
      .finally(() => setLoading(false));
  }, [runNo]);

  const rows = useMemo(() => entries.map(e => {
    const voluntaryDed = (e.deductions || [])
      .filter(d => !d.isStatutory)
      .reduce((s, d) => s + (d.amount || 0), 0);
    const threshold = (e.grossPay || 0) / 3;
    const violated  = voluntaryDed > threshold && threshold > 0;
    return { ...e, voluntaryDed, threshold, violated };
  }), [entries]);

  const violators = rows.filter(r => r.violated);

  if (!runNo) return <Alert severity="info">Select a run above.</Alert>;
  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Box mb={1.5} display="flex" alignItems="center" gap={2}>
        <Typography variant="body2" color="text.secondary">
          Kenya Employment Act §19: voluntary deductions must not exceed 1/3 of gross pay.
        </Typography>
        <Chip
          label={violators.length === 0 ? 'No violations' : `${violators.length} violation${violators.length > 1 ? 's' : ''}`}
          color={violators.length === 0 ? 'success' : 'error'}
          size="small"
        />
      </Box>

      {violators.length === 0 ? (
        <Alert severity="success">All {rows.length} employee{rows.length !== 1 ? 's' : ''} are within the one-third rule.</Alert>
      ) : (
        <Paper elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Emp No</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Dept</strong></TableCell>
                <TableCell align="right"><strong>Gross Pay</strong></TableCell>
                <TableCell align="right"><strong>1/3 Threshold</strong></TableCell>
                <TableCell align="right"><strong>Voluntary Ded.</strong></TableCell>
                <TableCell align="right"><strong>Excess</strong></TableCell>
                <TableCell><strong>Deductions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {violators.map(e => {
                const excess = e.voluntaryDed - e.threshold;
                const volLines = (e.deductions || []).filter(d => !d.isStatutory);
                return (
                  <TableRow key={e.employeeNo} sx={{ bgcolor: 'error.50' }}>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{e.employeeNo}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>{e.employeeName}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{e.department || '—'}</Typography></TableCell>
                    <TableCell align="right">{fmt(e.grossPay)}</TableCell>
                    <TableCell align="right">{fmt(e.threshold)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>{fmt(e.voluntaryDed)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>+{fmt(excess)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {volLines.map(d => `${d.description} (${sym()} ${fmt(d.amount)})`).join(' · ')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </>
  );
};

// ─── Transaction Variance Tab ──────────────────────────────────────────────────
const VarianceTab = ({ runNo, runs }) => {
  const [compareRunNo, setCompareRunNo] = useState('');
  const [currEntries,  setCurrEntries]  = useState([]);
  const [prevEntries,  setPrevEntries]  = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');

  useEffect(() => {
    if (!runNo) return;
    setLoading(true); setError('');
    axios.get(`${API}/api/payroll/runs/${runNo}/entries`, { headers: hdrs() })
      .then(r => setCurrEntries(r.data || []))
      .catch(() => setError('Failed to load current run entries'))
      .finally(() => setLoading(false));
  }, [runNo]);

  useEffect(() => {
    if (!compareRunNo) { setPrevEntries([]); return; }
    axios.get(`${API}/api/payroll/runs/${compareRunNo}/entries`, { headers: hdrs() })
      .then(r => setPrevEntries(r.data || []))
      .catch(() => {});
  }, [compareRunNo]);

  const variance = useMemo(() => {
    const prevMap = Object.fromEntries(prevEntries.map(e => [e.employeeNo, e]));
    return currEntries
      .filter(e => !search || e.employeeName?.toLowerCase().includes(search.toLowerCase()) || e.employeeNo?.toLowerCase().includes(search.toLowerCase()))
      .map(curr => {
        const prev = prevMap[curr.employeeNo];
        const allCodes = new Set([
          ...(curr.earnings   || []).map(l => l.code),
          ...(curr.deductions || []).map(l => l.code),
          ...(prev?.earnings   || []).map(l => l.code),
          ...(prev?.deductions || []).map(l => l.code),
        ]);
        const lines = [...allCodes].map(code => {
          const cE = [...(curr.earnings||[]), ...(curr.deductions||[])].find(l => l.code === code);
          const pE = prev ? [...(prev.earnings||[]), ...(prev.deductions||[])].find(l => l.code === code) : null;
          const currAmt = cE?.amount || 0;
          const prevAmt = pE?.amount || 0;
          const diff    = currAmt - prevAmt;
          return { code, description: cE?.description || pE?.description || code, prevAmt, currAmt, diff };
        }).filter(l => l.diff !== 0 || !compareRunNo);
        return { ...curr, lines, netDiff: (curr.netPay || 0) - (prev?.netPay || 0) };
      });
  }, [currEntries, prevEntries, compareRunNo, search]);

  if (!runNo) return <Alert severity="info">Select a run above.</Alert>;
  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error">{error}</Alert>;

  const otherRuns = runs.filter(r => r.runNo !== runNo);

  return (
    <>
      <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
        <TextField select label="Compare with run" size="small" value={compareRunNo}
          onChange={e => setCompareRunNo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
          sx={{ minWidth: 260 }}>
          <MenuItem value=""><em>— Show all lines (no comparison) —</em></MenuItem>
          {otherRuns.map(r => (
            <MenuItem key={r.runNo} value={r.runNo}>{r.runNo} · {r.periodName}</MenuItem>
          ))}
        </TextField>
        <TextField placeholder="Search employee…" size="small" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 0.5 }} /> }}
          sx={{ minWidth: 220 }} />
        {compareRunNo && (
          <Chip label={`Showing only changed lines`} size="small" color="info" variant="outlined" />
        )}
      </Box>

      <Paper elevation={1} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              {compareRunNo && <TableCell align="right"><strong>Previous</strong></TableCell>}
              <TableCell align="right"><strong>Current</strong></TableCell>
              {compareRunNo && <TableCell align="right"><strong>Change</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {variance.flatMap(emp =>
              emp.lines.map((line, li) => {
                const isNew  = compareRunNo && line.prevAmt === 0 && line.currAmt !== 0;
                const isGone = compareRunNo && line.currAmt === 0 && line.prevAmt !== 0;
                const isInc  = line.diff > 0;
                return (
                  <TableRow key={`${emp.employeeNo}-${line.code}`}
                    sx={{ bgcolor: isNew ? '#e8f5e9' : isGone ? '#fce4ec' : 'inherit' }}>
                    <TableCell>
                      {li === 0 && (
                        <>
                          <Typography variant="body2" fontWeight={600}>{emp.employeeName}</Typography>
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary">{emp.employeeNo}</Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{line.code}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{line.description}</Typography></TableCell>
                    {compareRunNo && <TableCell align="right" sx={{ color: 'text.secondary' }}>{line.prevAmt ? fmt(line.prevAmt) : '—'}</TableCell>}
                    <TableCell align="right" sx={{ color: isGone ? 'error.main' : 'inherit' }}>
                      {line.currAmt ? fmt(line.currAmt) : <Typography variant="caption" color="error.main">Removed</Typography>}
                    </TableCell>
                    {compareRunNo && (
                      <TableCell align="right" sx={{ fontWeight: 700, color: line.diff === 0 ? 'text.secondary' : isInc ? 'success.main' : 'error.main' }}>
                        {line.diff === 0 ? '—' : `${isInc ? '+' : ''}${fmt(line.diff)}`}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
            {variance.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary" py={2}>No data</Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const PayrollReportPage = () => {
  const navigate = useNavigate();

  const [runs,       setRuns]       = useState([]);
  const [runNo,      setRunNo]      = useState('');
  const [selectedRun, setSelectedRun] = useState(null);
  const [tab,        setTab]        = useState(0);
  const [runsLoading, setRunsLoading] = useState(true);
  const [error,      setError]      = useState('');

  useEffect(() => {
    axios.get(`${API}/api/payroll/runs`, { headers: hdrs() })
      .then(r => setRuns(r.data || []))
      .catch(() => setError('Failed to load runs'))
      .finally(() => setRunsLoading(false));
  }, []);

  useEffect(() => {
    if (runNo) setSelectedRun(runs.find(r => r.runNo === runNo) || null);
    else setSelectedRun(null);
  }, [runNo, runs]);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Tooltip title="Payroll Home" className="no-print">
                <IconButton size="small" onClick={() => navigate('/payroll')}
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                  <Home fontSize="small" />
                </IconButton>
              </Tooltip>
              <Assessment />
              <Typography variant="h6" fontWeight={700}>Payroll Reports</Typography>
              {selectedRun && (
                <Chip label={`${selectedRun.periodName} · ${selectedRun.status}`} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Run selector */}
      <Card elevation={2} sx={{ mb: 2 }} className="no-print">
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField select label="Payroll Run" size="small" value={runNo}
              onChange={e => setRunNo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
              disabled={runsLoading}
              sx={{ minWidth: 280 }}>
              <MenuItem value=""><em>{runsLoading ? 'Loading runs…' : '— Select a run —'}</em></MenuItem>
              {runs.map(r => (
                <MenuItem key={r.runNo} value={r.runNo}>
                  {r.runNo} · {r.periodName} · {r.status}
                </MenuItem>
              ))}
            </TextField>
            {selectedRun && (
              <Box display="flex" gap={2} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Head Count: {selectedRun.headCount || '—'} &nbsp;|&nbsp;
                  Gross: {sym()} {fmtN(selectedRun.totalGross)} &nbsp;|&nbsp;
                  Net: {sym()} {fmtN(selectedRun.totalNetPay)}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Report tabs */}
      <Card elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Payroll Register" />
            <Tab label="Statutory Summary" />
            <Tab label="By Component" />
            <Tab label="P9A (Annual)" />
            <Tab label="Bank File" />
            <Tab label="Variance" />
            <Tab label="A-Third Rule" />
          </Tabs>
        </Box>
        <CardContent>
          <TabPanel value={tab} index={0}><RegisterTab    runNo={runNo} /></TabPanel>
          <TabPanel value={tab} index={1}><StatutoryTab   runNo={runNo} /></TabPanel>
          <TabPanel value={tab} index={2}><ByComponentTab runNo={runNo} /></TabPanel>
          <TabPanel value={tab} index={3}><P9ATab         runNo={runNo} /></TabPanel>
          <TabPanel value={tab} index={4}><BankFileTab    runNo={runNo} /></TabPanel>
          <TabPanel value={tab} index={5}><VarianceTab    runNo={runNo} runs={runs} /></TabPanel>
          <TabPanel value={tab} index={6}><AThirdRuleTab  runNo={runNo} /></TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PayrollReportPage;
